import Component from '@glimmer/component'
import RouteTemplate from 'ember-route-template'
import writingPitch from '@images/writing-pitch-item.svg'
import Disappointed from '@images/emoji-disappointed.svg'
import Smile from '@images/emoji-smile.svg'
import Unsure from '@images/emoji-unsure.svg'
import PageHeader from 'teacher-ui-client/components/page-header/index.gts'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon.gts'
import { on } from '@ember/modifier'
import { t, type IntlService } from 'ember-intl'
import themeKey from 'teacher-ui-client/helpers/theme-key'
import type SubscriptionTypeService from 'teacher-ui-client/services/subscription-type'
import { service } from '@ember/service'
import type EWISubmissionRoute from 'teacher-ui-client/subscription-type/writing-ideas/submission/route'
import { htmlSafe } from '@ember/template'
import TeacherFeedbacksSection from 'teacher-ui-client/components/writing-legends/teacher-feedbacks-section.gts'
import ImageBlock from 'teacher-ui-client/components/writing-legends/activity-blocks/image-block.gts'
import { PageHeaderBackButton } from 'teacher-ui-client/components/page-header/back-button.gts'

const ReflectionImages: { [key: string]: typeof Smile } = {
  smile: Smile,
  unsure: Unsure,
  disappointed: Disappointed,
}

interface Signature {
  Args: {
    model: ModelFor<EWISubmissionRoute>
  }
}

export class ExcitingWritingIdeasSubmissionPage extends Component<Signature> {
  @service subscriptionType!: SubscriptionTypeService
  @service intl!: IntlService

  get activityId() {
    return this.args.model?.params?.activity_id
  }

  get studentId() {
    return this.args.model?.params?.student_id
  }

  get snapshotId() {
    return this.args.model?.params?.snapshot_id
  }

  get resultId() {
    return this.args.model?.submission?.result_id
  }

  get score() {
    return `${this.args.model?.submission?.score}/100`
  }

  get words() {
    return this.args.model?.submission?.words
  }

  get activityTitle() {
    return this.args.model?.activity?.title
  }

  get imageBlock() {
    return this.args.model?.activity?.blocks?.find((block) => block.block_type === 'Image')
  }

  get activityTags(): string | undefined {
    return this.args.model?.activity?.tags
  }

  get targetWordCount() {
    return this.args.model?.submission?.legends_feedback?.[0]?.target_word_count
  }

  get reflectionEmoji() {
    if (!this.args.model?.submission?.reflection) return null
    try {
      const reflectionObject = JSON.parse(this.args.model?.submission?.reflection) as {
        emoji: 'smile' | 'unsure' | 'disappointed'
      }
      return ReflectionImages[reflectionObject.emoji]
    } catch {
      return null
    }
  }

  get lastUpdatedDate() {
    return this.intl.formatDate(this.args.model?.submission?.updated_date, {
      locale: 'en-AU',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })
  }

  get answerText() {
    // Answer text contains rich text formatting, and the html is sanitized on the backend, so it should be safe to render as HTML
    return this.args.model?.submission?.answer_snapshots?.[0]?.answer_text ?? ''
  }

  get studentFullName() {
    const student = this.args.model?.student
    if (!student) return ''
    return `${student.first_name} ${student.last_name}`
  }

  print() {
    window.print()
  }

  <template>
    <div class="grow">
      <PageHeader>
        <:left>
          <PageHeaderBackButton class="cursor-pointer" />
        </:left>
        <:title>
          <h2 data-test-activity-title>
            {{this.activityTitle}}
          </h2>
        </:title>
      </PageHeader>
      <div class="mx-auto w-3/4">
        <div class="m-10">
          <h3 class="m-5 text-center text-2xl" data-test-student-name>{{this.studentFullName}}</h3>

          <div class="flex justify-center text-lg">
            <span class="ml-2 text-left text-[#777] uppercase">
              {{t "pages.recentWriting.viewWriting.instantLegendFeedback"}}
            </span>
            <div class="flex items-center justify-center">
              <div class="ml-2 flex justify-end">
                <img src={{writingPitch}} alt={{t "pages.accessMissing.writing.coinAlt"}} width="30" height="30" />
              </div>
              <span class="text-wl-blue-300 ml-2 text-left" data-test-score>{{this.score}}</span>
            </div>
          </div>
        </div>

        <div class="flex justify-center">
          <div class="flex w-3/4 flex-col justify-between text-center md:flex-row">
            <div>
              <span class="text-[#777] uppercase">{{t "pages.recentWriting.viewWriting.lastUpdated"}}</span>
              {{this.lastUpdatedDate}}
            </div>
            <div>
              <span class="text-[#777] uppercase">{{t "pages.recentWriting.viewWriting.totalWords"}}</span>
              <span data-test-total-words>{{this.words}}</span>
            </div>

            <div class="flex">
              <span class="mr-1 text-[#777] uppercase">
                {{t "pages.recentWriting.viewWriting.studentReflection"}}
              </span>
              <img
                src={{this.reflectionEmoji}}
                width="20"
                height="20"
                alt="{{t 'pages.recentWriting.viewWriting.reflectionEmoji'}}"
              />
            </div>

          </div>

        </div>

        {{#if this.imageBlock}}
          <ImageBlock class="my-10" @data={{this.imageBlock}} @tags={{this.activityTags}} />
        {{/if}}

        <div class="flex justify-center">
          <button
            type="button"
            class="rounded-md border-2 border-blue-400 p-2 text-blue-400 print:hidden"
            {{on "click" this.print}}
          ><PhosphorIcon @icon="phosphor-printer" /><span class="px-2">{{t "print"}}</span></button>
        </div>

        <div>
          <div class="mt-6 border-2 border-[#b0edf5]">
            <div class="border-b-2 border-[#b0edf5] p-2 text-center font-bold">{{t
                "pages.recentWriting.viewWriting.targetWordCount"
              }}:
              <span data-test-target-word-count>{{this.targetWordCount}}</span></div>
            <div class="min-h-52 bg-[#f1f9ff] p-2" data-test-answer-text>{{htmlSafe this.answerText}}</div>
          </div>
          <div class="p-2 text-right">{{t "pages.recentWriting.viewWriting.words"}}:
            {{this.words}}/{{this.targetWordCount}}</div>
        </div>

        <TeacherFeedbacksSection
          data-test-feedback-section
          class="mt-14 print:hidden"
          @themeKey={{themeKey this.subscriptionType.current}}
          @studentId={{this.studentId}}
          @resultId={{this.resultId}}
          @snapshotId={{this.snapshotId}}
        />
      </div>
    </div>
  </template>
}

export default RouteTemplate(ExcitingWritingIdeasSubmissionPage)
